import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Card,
    CardMedia,
    CardActionArea,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Tabs,
    Tab
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api'; // Assuming 'api' is still used for fetching/uploading
import { useToast } from '../../context/ToastContext';

/**
 * Interface representing a media file
 */
interface MediaFile {
    _id: string;
    filename: string;
    path: string;
    mimetype: string;
    size: number;
    url: string;
    createdAt: string;
}

/**
 * Props for the MediaLibrary component
 */
interface MediaLibraryProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Function to call when the dialog is closed */
    onClose: () => void;
    /** Function to call when a media file is selected */
    onSelect: (url: string) => void;
    /** Whether to allow multiple selection (not yet implemented in this version) */
    multiselect?: boolean;
    /** Filter by media type */
    mediaType?: 'image' | 'video' | 'all';
}

/**
 * Media Library component for uploading, viewing, and selecting media files.
 *
 * @param props - The props for the component
 * @returns The Media Library UI
 */
const MediaLibrary: React.FC<MediaLibraryProps> = ({ open, onClose, onSelect, mediaType = 'all' }) => {
    const [tab, setTab] = useState(0);
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const { showToast } = useToast();

    useEffect(() => {
        if (open && tab === 1) {
            fetchMedia();
        }
    }, [open, tab]);

    const fetchMedia = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (searchTerm) params.search = searchTerm;
            if (mediaType !== 'all') params.type = mediaType;

            const res = await api.get('/api/media', { params });
            setMediaFiles(res.data.data || []);
        } catch (err) {
            console.error("Error fetching media", err);
            showToast('Error fetching media', 'error');
        } finally {
            setLoading(false);
        }
    };

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', acceptedFiles[0]);

            const res = await api.post('/api/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            console.log('Upload response:', res.data);
            if (res.data.url) {
                onSelect(res.data.url);
                showToast('File uploaded successfully', 'success');
                // Refresh media library
                fetchMedia();
                // Switch to library tab to show the uploaded image
                setTab(1);
            } else {
                showToast('Upload failed: No URL returned', 'error');
            }
        } catch (err: any) {
            console.error("Error uploading file", err);
            showToast(`Upload failed: ${err.response?.data?.message || err.message} `, 'error');
        } finally {
            setUploading(false);
        }
    }, [onSelect, showToast]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: mediaType === 'video'
            ? { 'video/*': [] }
            : mediaType === 'image'
                ? { 'image/*': [] }
                : { 'image/*': [], 'video/*': [] },
        multiple: false
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Media Library</DialogTitle>
            <DialogContent>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab label="Upload" />
                    <Tab label="Library" />
                    <Tab label="Insert from URL" />
                </Tabs>

                {tab === 0 && (
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed #ccc',
                            p: 5,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: isDragActive ? '#f0f0f0' : 'transparent'
                        }}
                    >
                        <input {...getInputProps()} />
                        {uploading ? (
                            <CircularProgress />
                        ) : (
                            <Typography>
                                {isDragActive
                                    ? "Drop the file here"
                                    : `Drag & drop ${mediaType === 'image' ? 'an image' : mediaType === 'video' ? 'a video' : 'a file'} here, or click to select`
                                }
                            </Typography>
                        )}
                    </Box>
                )}

                {tab === 1 && (
                    <Box sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        fetchMedia();
                                    }
                                }}
                            />
                            <Button variant="contained" onClick={fetchMedia}>
                                Search
                            </Button>
                        </Box>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                            {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
                                <Grid container spacing={2}>
                                    {mediaFiles.length === 0 ? (
                                        <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
                                            <Typography color="text.secondary">No media files found.</Typography>
                                        </Box>
                                    ) : (
                                        mediaFiles.map((file) => (
                                            <Grid key={file._id} size={{ xs: 4, sm: 3 }}>
                                                <Card sx={{ position: 'relative' }}>
                                                    <CardActionArea onClick={() => {
                                                        console.log('Selected file:', file.url);
                                                        onSelect(file.url);
                                                        onClose();
                                                    }}>
                                                        {file.mimetype && file.mimetype.startsWith('video/') ? (
                                                            <Box sx={{ height: 100, bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Typography variant="h4" color="white">▶</Typography>
                                                            </Box>
                                                        ) : (
                                                            <CardMedia
                                                                component="img"
                                                                height="100"
                                                                image={file.url}
                                                                alt={file.filename}
                                                                sx={{ objectFit: 'cover' }}
                                                                onError={(e: any) => {
                                                                    console.error('Image load error:', file.url);
                                                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EError%3C/text%3E%3C/svg%3E';
                                                                }}
                                                            />
                                                        )}
                                                        <Typography variant="caption" sx={{ p: 0.5, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {file.filename}
                                                        </Typography>
                                                    </CardActionArea>
                                                </Card>
                                            </Grid>
                                        ))
                                    )}
                                </Grid>
                            )}
                        </Box>
                    </Box>
                )}

                {tab === 2 && (
                    <Box sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            label="Image URL"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            sx={{ mt: 2 }}
                            onClick={() => {
                                if (urlInput) {
                                    onSelect(urlInput);
                                    onClose();
                                }
                            }}
                        >
                            Insert
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MediaLibrary;
